import React, { type ReactElement, useEffect, useState } from 'react';
import { Box, Icon, TabGroup, TabItem } from '@saviynt/design-system';

import { VIOLATIONS_LIST_SIDEPANEL_TABS } from '../../../../shared/constants';
import {
  useInvertedLocalize,
  useTypedSelector,
} from '../../../../shared/hooks';

const TRANSITION_DELAY_IN_MS = 250;

interface ViolationsSidePanelTabsProps {
  activeTab: string;
  handleChange: (tab: string) => void;
  sidepanelRef: React.RefObject<HTMLDivElement>;
}

/**
 * ViolationsSidePanelTabs
 *
 * A tabbed interface component for displaying violations in a side panel.
 * This component manages the tab navigation and content display for different violation
 * categories or views within the side panel of the violations interface.
 *
 * @example
 * ```tsx
 * <ViolationsSidePanelTabs
 *   activeTab="details"
 *   onTabChange={handleTabChange}
 * />
 * ```
 */
function ViolationsSidePanelTabs({
  activeTab,
  handleChange,
  sidepanelRef,
}: ViolationsSidePanelTabsProps): ReactElement {
  // Delay Tabs render by TRANSITION_DELAY_IN_MS after sidepanel opens
  const [isTabsReady, setIsTabsReady] = useState(false);

  const l = useInvertedLocalize();

  const {
    sidepanel: { isSidePanelVisible },
  } = useTypedSelector((state) => state.aag.violationsTab);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;

    if (isSidePanelVisible) {
      timer = setTimeout(() => {
        setIsTabsReady(true);
      }, TRANSITION_DELAY_IN_MS);
    } else {
      setIsTabsReady(false);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [isSidePanelVisible]);

  if (!isTabsReady) {
    return <Box className='Sod-ViolationsSidePanelTabs--isOpening' />;
  }

  return (
    <TabGroup
      moreContainerRef={sidepanelRef}
      value={activeTab}
      onChange={handleChange}>
      <TabItem
        label={l('Summary')}
        value={VIOLATIONS_LIST_SIDEPANEL_TABS.TAB_SUMMARY}
        icon={<Icon kind='Document' size='medium' />}
      />
      <TabItem
        label={l('History')}
        value={VIOLATIONS_LIST_SIDEPANEL_TABS.TAB_HISTORY}
        icon={<Icon kind='Time' size='medium' />}
      />
      <TabItem
        label={l('User')}
        value={VIOLATIONS_LIST_SIDEPANEL_TABS.TAB_USER}
        icon={<Icon kind='User' size='medium' />}
      />
    </TabGroup>
  );
}

export default ViolationsSidePanelTabs;
